"use client";

import { ArrowUpRight } from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";

import { ButtonLink } from "@/components/ButtonLink";
import {
  FILM_SECTION_MUTED_CLASS,
  SectionSlate,
  SplitTitle,
  useInViewOnce,
  useScrollProgress,
} from "@/components/Film";
import { Link } from "@/i18n/navigation";
import { getSmoothScroller } from "@/lib/smooth-scroll";
import { cn } from "@/lib/utils";

import { FeatureShowcaseVisual } from "../../home-feature-showcase/FeatureShowcaseVisual";
import { homeFeatureShowcaseVisualScopeClass } from "../../home-feature-showcase/home-feature-showcase.constants";
import type { ReelFrame } from "./the-reel.types";
import {
  formatReelCounter,
  getReelActiveIndex,
  getReelPlayingIndex,
  getReelProgressFor,
} from "./the-reel.utils";

/** Idle time after the last scroll before the strip settles on a frame. */
const REEL_SETTLE_MS = 180;

type TheReelStageProps = {
  frames: ReelFrame[];
  slateLabel: string;
  title: string;
  sync: string;
  viewAllLabel: string;
};

const REEL_MEDIA =
  "(min-width: 1024px) and (prefers-reduced-motion: no-preference)";

export const TheReelStage = ({
  frames,
  slateLabel,
  title,
  sync,
  viewAllLabel,
}: TheReelStageProps) => {
  const rootRef = useRef<HTMLElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const frameRefs = useRef<Array<HTMLLIElement | null>>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [playingIndex, setPlayingIndex] = useState<number | null>(0);
  const [pinned, setPinned] = useState(false);
  // Nothing mounts until the section is close: no demo JS at page load.
  const near = useInViewOnce(rootRef, "300px 0px 300px 0px");
  // Frames stay on screen once reached, paused unless centered. The next and
  // previous frames load early so none arrives as a blank.
  const [mounted, setMounted] = useState<ReadonlySet<number>>(() => new Set());
  useEffect(() => {
    if (!near) return;
    setMounted((current) => {
      const wanted = [activeIndex - 1, activeIndex, activeIndex + 1].filter(
        (index) => index >= 0 && index < frames.length && !current.has(index),
      );
      return wanted.length ? new Set([...current, ...wanted]) : current;
    });
  }, [near, activeIndex, frames.length]);

  useEffect(() => {
    const query = window.matchMedia(REEL_MEDIA);
    const update = () => setPinned(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  // Pinned: scroll moves the strip. The nearest frame drives the counter; a
  // frame only plays once it fills most of the center.
  const onProgress = useCallback(
    (progress: number) => {
      rootRef.current?.style.setProperty("--reel", String(progress));
      setActiveIndex(getReelActiveIndex(progress, frames.length));
      setPlayingIndex(getReelPlayingIndex(progress, frames.length));
    },
    [frames.length],
  );
  useScrollProgress(trackRef, onProgress, "sticky", pinned);

  // Pinned: when scrolling stops between two frames, glide to the nearest one.
  useEffect(() => {
    if (!pinned) return;
    let timer = 0;
    const settle = () => {
      const track = trackRef.current;
      if (!track) return;
      const rect = track.getBoundingClientRect();
      const distance = rect.height - window.innerHeight;
      const progress = -rect.top / distance;
      if (progress <= 0 || progress >= 1) return;
      const nearest = getReelActiveIndex(progress, frames.length);
      const target = getReelProgressFor(nearest, frames.length);
      if (Math.abs(target - progress) * (frames.length - 1) < 0.01) return;
      const y = window.scrollY + rect.top + target * distance;
      const scroller = getSmoothScroller();
      if (scroller) scroller.scrollTo(y);
      else window.scrollTo({ top: y, behavior: "smooth" });
    };
    const onScroll = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(settle, REEL_SETTLE_MS);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.clearTimeout(timer);
    };
  }, [pinned, frames.length]);

  // Stacked: the frame most in view is current; it plays once 75% is visible.
  useEffect(() => {
    if (pinned || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting);
        const best = visible.sort(
          (a, b) => b.intersectionRatio - a.intersectionRatio,
        )[0];
        if (!best) return;
        const index = Number((best.target as HTMLElement).dataset.index);
        setActiveIndex(index);
        setPlayingIndex(best.intersectionRatio >= 0.75 ? index : null);
      },
      { threshold: [0.25, 0.5, 0.75, 1] },
    );
    frameRefs.current.forEach((frame) => frame && observer.observe(frame));
    return () => observer.disconnect();
  }, [pinned]);

  // Keyboard: focusing a link in an off-center frame scrolls that frame to center.
  const focusFrame = (index: number) => {
    if (!pinned || index === activeIndex || !trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const distance = rect.height - window.innerHeight;
    const y =
      window.scrollY +
      rect.top +
      getReelProgressFor(index, frames.length) * distance;
    const scroller = getSmoothScroller();
    if (scroller) scroller.scrollTo(y, { immediate: true });
    else window.scrollTo({ top: y });
  };

  return (
    <section
      ref={rootRef}
      id="product"
      aria-labelledby="film-reel-title"
      className={cn(FILM_SECTION_MUTED_CLASS, "film-reel")}
    >
      <div ref={trackRef} className="film-reel-track">
        <div className="film-reel-stage">
          <div className="marketing-grid flex flex-wrap items-end justify-between gap-6 pb-10 lg:pt-[calc(0.625rem+3.5rem)]">
            <div className="flex max-w-2xl flex-col gap-3">
              <SectionSlate index={2} label={slateLabel} />
              <SplitTitle
                id="film-reel-title"
                className="font-display text-3xl font-semibold tracking-tight text-balance sm:text-4xl"
              >
                {title}
              </SplitTitle>
              <p className="text-muted-foreground">{sync}</p>
            </div>
            <p
              aria-hidden
              className="hidden font-display text-2xl font-semibold tabular-nums lg:block"
            >
              {formatReelCounter(activeIndex, frames.length)}
            </p>
          </div>

          <ol className="film-reel-row">
            {frames.map((frame, index) => {
              const isPlaying = index === playingIndex;
              return (
                <li
                  key={frame.id}
                  ref={(node) => {
                    frameRefs.current[index] = node;
                  }}
                  data-index={index}
                  onFocusCapture={() => focusFrame(index)}
                  className="film-reel-frame grid gap-6 lg:grid-cols-[15rem_1fr] lg:gap-10"
                  style={{ "--i": index } as CSSProperties}
                >
                  <div className="flex flex-col gap-3 lg:justify-center">
                    <p className="text-xs font-medium uppercase tabular-nums tracking-[0.24em] text-muted-foreground">
                      {formatReelCounter(index, frames.length)} ·{" "}
                      {frame.eyebrow}
                    </p>
                    <h3 className="font-display text-xl font-semibold tracking-tight text-balance sm:text-2xl">
                      {frame.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {frame.description}
                    </p>
                    <Link
                      href={frame.href}
                      className="inline-flex w-fit items-center gap-1 text-sm font-medium underline-offset-4 hover:underline"
                    >
                      {frame.linkLabel}
                      <ArrowUpRight className="size-4" aria-hidden />
                    </Link>
                  </div>

                  <div className="film-strip overflow-hidden rounded-md">
                    <div className="film-sprockets" aria-hidden />
                    <div
                      data-reel-live={near && isPlaying ? "" : undefined}
                      className={cn(
                        "relative aspect-[16/10] overflow-hidden bg-black",
                        homeFeatureShowcaseVisualScopeClass,
                      )}
                    >
                      {mounted.has(index) ? (
                        <FeatureShowcaseVisual
                          featureId={frame.id}
                          className="h-full"
                          isPaused={!isPlaying}
                        />
                      ) : (
                        <div
                          aria-hidden
                          className="absolute inset-0 grid place-items-center"
                        >
                          <span className="font-display text-4xl font-semibold uppercase tracking-tight text-white/15 sm:text-6xl">
                            {frame.eyebrow}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="film-sprockets" aria-hidden />
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </div>

      <div className="marketing-grid flex flex-wrap items-center justify-end gap-4 pb-20 pt-10 lg:pt-0">
        <ButtonLink href="/features" variant="outline" className="w-fit">
          {viewAllLabel}
        </ButtonLink>
      </div>
    </section>
  );
};
